// Paywall event bus.
//
// The server returns `402 { code: "entitlement_required", slug,
// paywallEnabled }` when a tenant tries to use a feature their plan
// does not include (see artifacts/api-server/src/lib/entitlements.ts).
// Any client-side caller that hits a 402 with `code === "entitlement_required"`
// publishes a paywall event through this module, and the global
// `<PaywallModal>` (mounted in App.tsx) renders an upgrade prompt.
//
// The bus is intentionally framework-free so it can be called from
// React Query cache callbacks, the orval-generated fetch client, raw
// `fetch()` call sites, and tests alike — none of them need to know
// about React.

export interface PaywallPayload {
  slug: string;
  paywallEnabled: boolean;
}

type PaywallListener = (payload: PaywallPayload) => void;

const listeners = new Set<PaywallListener>();

export function subscribePaywall(listener: PaywallListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitPaywall(payload: PaywallPayload): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener(payload);
    } catch {
      // Listener errors must never break the original request flow.
    }
  }
}

// Narrow an unknown value (e.g. `ApiError.data` or a parsed JSON body)
// to a paywall payload. Returns null when the shape doesn't match the
// `entitlement_required` 402 contract, so callers can keep their normal
// error handling.
export function paywallPayloadFromBody(body: unknown): PaywallPayload | null {
  if (!body || typeof body !== "object") return null;
  const record = body as Record<string, unknown>;
  if (record.code !== "entitlement_required") return null;
  const slug = typeof record.slug === "string" ? record.slug : "";
  const paywallEnabled = record.paywallEnabled === true;
  return { slug, paywallEnabled };
}
